import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../config.js';
import { FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaChartLine, FaStar, FaGift, FaCrown, FaExclamationTriangle, FaTrashAlt } from 'react-icons/fa';
import { MdVerifiedUser } from 'react-icons/md';
import { BsStars } from 'react-icons/bs';

const AgentAccount = ({ userToken, onLogout }) => {
    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        fetchAccountInfo();
    }, []);

    const fetchAccountInfo = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(`${apiBaseUrl}/api/agent/account`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`
                }
            });

            if (response.data.success) {
                setAccount(response.data.account);
            }
        } catch (err) {
            console.error('Error fetching account info:', err);
            setError(err.response?.data?.error || 'Error al cargar información de la cuenta');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        try {
            setDeleteLoading(true);

            const response = await axios.delete(`${apiBaseUrl}/api/agent/account`, {
                headers: {
                    'Authorization': `Bearer ${userToken}`
                }
            });

            if (response.data.success) {
                alert('Tu cuenta ha sido eliminada exitosamente. Serás redirigido al inicio.');
                onLogout(); // Log out and redirect
            }
        } catch (err) {
            console.error('Error deleting account:', err);
            alert(err.response?.data?.error || 'Error al eliminar la cuenta. Por favor, intenta de nuevo.');
        } finally {
            setDeleteLoading(false);
            setShowDeleteModal(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <div style={{
                    fontSize: '18px',
                    color: '#667eea'
                }}>
                    Cargando...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                padding: '40px 20px',
                textAlign: 'center'
            }}>
                <div style={{
                    fontSize: '18px',
                    color: '#e74c3c',
                    marginBottom: '10px'
                }}>
                    {error}
                </div>
                <button
                    onClick={fetchAccountInfo}
                    style={{
                        marginTop: '20px',
                        padding: '12px 24px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#2c3e50',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
            }}>
                <FaUser size={28} color="#667eea" />
                Mi Cuenta
            </h2>

            {/* Account Information Card */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e9ecef',
                marginBottom: '30px'
            }}>
                <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    marginBottom: '25px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <MdVerifiedUser size={24} color="#667eea" />
                    Información Personal
                </h3>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth > 768 ? 'repeat(2, 1fr)' : '1fr',
                    gap: '20px'
                }}>
                    {/* Name */}
                    <div style={{
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '10px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px',
                            color: '#6c757d',
                            fontSize: '14px',
                            fontWeight: '600'
                        }}>
                            <FaUser size={14} />
                            Nombre
                        </div>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#2c3e50'
                        }}>
                            {account?.name} {account?.lname}
                        </div>
                    </div>

                    {/* Email */}
                    <div style={{
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '10px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px',
                            color: '#6c757d',
                            fontSize: '14px',
                            fontWeight: '600'
                        }}>
                            <FaEnvelope size={14} />
                            Email
                        </div>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#2c3e50',
                            wordBreak: 'break-word'
                        }}>
                            {account?.email}
                        </div>
                    </div>

                    {/* Phone */}
                    {account?.phone && (
                        <div style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '8px',
                                color: '#6c757d',
                                fontSize: '14px',
                                fontWeight: '600'
                            }}>
                                <FaPhone size={14} />
                                Teléfono
                            </div>
                            <div style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#2c3e50'
                            }}>
                                {account.phone}
                            </div>
                        </div>
                    )}

                    {/* Member Since */}
                    <div style={{
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '10px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px',
                            color: '#6c757d',
                            fontSize: '14px',
                            fontWeight: '600'
                        }}>
                            <FaCalendarAlt size={14} />
                            Miembro desde
                        </div>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#2c3e50'
                        }}>
                            {formatDate(account?.memberSince)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistics Card */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e9ecef',
                marginBottom: '30px'
            }}>
                <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    marginBottom: '25px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <FaChartLine size={24} color="#667eea" />
                    Estadísticas
                </h3>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth > 768 ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
                    gap: '20px'
                }}>
                    {/* Premium Credits */}
                    <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '12px',
                        textAlign: 'center',
                        color: 'white'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '10px'
                        }}>
                            <FaCrown size={30} />
                        </div>
                        <div style={{
                            fontSize: '32px',
                            fontWeight: 'bold',
                            marginBottom: '5px'
                        }}>
                            {account?.premiumCredits || 0}
                        </div>
                        <div style={{
                            fontSize: '13px',
                            opacity: 0.9
                        }}>
                            Créditos Disponibles
                        </div>
                    </div>

                    {/* Total Valuations */}
                    <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        borderRadius: '12px',
                        textAlign: 'center',
                        color: 'white'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '10px'
                        }}>
                            <FaChartLine size={30} />
                        </div>
                        <div style={{
                            fontSize: '32px',
                            fontWeight: 'bold',
                            marginBottom: '5px'
                        }}>
                            {account?.stats?.totalValuations || 0}
                        </div>
                        <div style={{
                            fontSize: '13px',
                            opacity: 0.9
                        }}>
                            Valuaciones Totales
                        </div>
                    </div>

                    {/* Premium Valuations */}
                    <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                        borderRadius: '12px',
                        textAlign: 'center',
                        color: 'white'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '10px'
                        }}>
                            <FaStar size={30} />
                        </div>
                        <div style={{
                            fontSize: '32px',
                            fontWeight: 'bold',
                            marginBottom: '5px'
                        }}>
                            {account?.stats?.premiumValuations || 0}
                        </div>
                        <div style={{
                            fontSize: '13px',
                            opacity: 0.9
                        }}>
                            Premium Pagadas
                        </div>
                    </div>

                    {/* Free Valuations */}
                    <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                        borderRadius: '12px',
                        textAlign: 'center',
                        color: 'white'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '10px'
                        }}>
                            <BsStars size={30} />
                        </div>
                        <div style={{
                            fontSize: '32px',
                            fontWeight: 'bold',
                            marginBottom: '5px'
                        }}>
                            {account?.stats?.freeValuations || 0}
                        </div>
                        <div style={{
                            fontSize: '13px',
                            opacity: 0.9
                        }}>
                            Con Créditos Gratis
                        </div>
                    </div>
                </div>
            </div>

            {/* Danger Zone */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 4px 12px rgba(231, 76, 60, 0.15)',
                border: '2px solid #fee',
                marginBottom: '30px'
            }}>
                <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#e74c3c',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <FaExclamationTriangle size={24} />
                    Zona de Peligro
                </h3>

                <p style={{
                    color: '#6c757d',
                    marginBottom: '20px',
                    lineHeight: '1.6'
                }}>
                    Una vez que elimines tu cuenta, no hay vuelta atrás. Por favor, estate seguro.
                    Todas tus valuaciones y datos serán eliminados permanentemente.
                </p>

                <button
                    onClick={() => setShowDeleteModal(true)}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#c0392b';
                        e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#e74c3c';
                        e.currentTarget.style.transform = 'scale(1)';
                    }}
                >
                    <FaTrashAlt size={16} />
                    Eliminar Mi Cuenta
                </button>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '30px',
                        maxWidth: '500px',
                        width: '100%',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '20px'
                        }}>
                            <FaExclamationTriangle size={50} color="#e74c3c" />
                        </div>

                        <h3 style={{
                            fontSize: '24px',
                            fontWeight: '700',
                            color: '#2c3e50',
                            marginBottom: '15px',
                            textAlign: 'center'
                        }}>
                            ¿Estás seguro?
                        </h3>

                        <p style={{
                            color: '#6c757d',
                            marginBottom: '25px',
                            textAlign: 'center',
                            lineHeight: '1.6'
                        }}>
                            Esta acción no se puede deshacer. Tu cuenta y todas tus valuaciones 
                            serán eliminadas permanentemente.
                        </p>

                        <div style={{
                            display: 'flex',
                            gap: '15px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleteLoading}
                                style={{
                                    padding: '12px 30px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: deleteLoading ? 'not-allowed' : 'pointer',
                                    opacity: deleteLoading ? 0.5 : 1
                                }}
                            >
                                Cancelar
                            </button>

                            <button
                                onClick={handleDeleteAccount}
                                disabled={deleteLoading}
                                style={{
                                    padding: '12px 30px',
                                    backgroundColor: '#e74c3c',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: deleteLoading ? 'not-allowed' : 'pointer',
                                    opacity: deleteLoading ? 0.5 : 1
                                }}
                            >
                                {deleteLoading ? 'Eliminando...' : 'Sí, Eliminar Mi Cuenta'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentAccount;
